import type { StoryListItem, SceneListItem, RealmId, StoryVisibilityTier } from "@chuzi/shared/types";
import { resolveStoryVisibilityTier, isStoryJettisoned } from "@chuzi/shared/types";

export type NavLevel = "nebula" | "story" | "scene" | "choice";

export type NavTier = StoryVisibilityTier | "jettisoned" | "chuzi";

export const NEBULA_TIERS: NavTier[] = ["private", "limited", "worldwide", "jettisoned"];

export const CHUZI_NEBULA_TIER: NavTier = "chuzi";

/**
 * Spatial left-to-right order of nebula indices for horizontal navigation.
 * Maps to increasing X position: private(-64) → limited(0) → worldwide(+64) → jettisoned(+96).
 */
export const NEBULA_SPATIAL_NAV_ORDER: number[] = [0, 1, 2, 3];

/** Mine-mode nav when the CHUZI platform nebula is present (admin system stories). */
export const MINE_NEBULA_TIERS_WITH_CHUZI: NavTier[] = [
  "chuzi",
  "private",
  "limited",
  "worldwide",
  "jettisoned",
];

/** Shipyard → relay → broadcast → CHUZI → jettison. */
export const MINE_NEBULA_SPATIAL_NAV_ORDER_WITH_CHUZI: number[] = [1, 2, 3, 0, 4];

export function buildMineNebulaNav(hasChuziNebula: boolean): {
  nebulaTiers: NavTier[];
  nebulaSpatialNavOrder: number[];
} {
  if (!hasChuziNebula) {
    return {
      nebulaTiers: NEBULA_TIERS,
      nebulaSpatialNavOrder: NEBULA_SPATIAL_NAV_ORDER,
    };
  }
  return {
    nebulaTiers: MINE_NEBULA_TIERS_WITH_CHUZI,
    nebulaSpatialNavOrder: MINE_NEBULA_SPATIAL_NAV_ORDER_WITH_CHUZI,
  };
}

function tierAtIndex(ctx: NavContext, nebulaIndex: number): NavTier | undefined {
  return ctx.nebulaTiers[nebulaIndex];
}

export interface BreadcrumbEntry {
  level: NavLevel;
  label: string;
  tierIndex?: number;
  storyIndex?: number;
}

export interface CosmosNavState {
  level: NavLevel;
  nebulaIndex: number;
  storyIndex: number;
  sceneIndex: number;
  choiceIndex: number;
  breadcrumb: BreadcrumbEntry[];
}

export type NavAction =
  | { type: "LEFT" }
  | { type: "RIGHT" }
  | { type: "DOWN" }
  | { type: "UP" }
  | { type: "JUMP_TO_LEVEL"; level: NavLevel }
  | { type: "RESET"; nebulaIndex?: number };

export interface NavContext {
  storiesByTier: Record<NavTier, StoryListItem[]>;
  nebulaTiers: NavTier[];
  nebulaSpatialNavOrder: number[];
  scenesByStory: Record<string, SceneListItem[]>;
  choicesForScene: string[];
  nebulaLabels: string[];
  realm: RealmId;
}

export function initialNavState(nebulaIndex = 0): CosmosNavState {
  return {
    level: "nebula",
    nebulaIndex,
    storyIndex: 0,
    sceneIndex: 0,
    choiceIndex: 0,
    breadcrumb: [],
  };
}

export function findInitialNebulaIndex(
  stories: StoryListItem[] | null,
  nebulaTiers: NavTier[] = NEBULA_TIERS,
): number {
  if (!stories?.length) return 0;

  if (nebulaTiers.includes("chuzi")) {
    const systemStories = stories.filter(
      (story) => story.is_system && !isStoryJettisoned(story),
    );
    const liveUserStories = stories.filter(
      (story) => !story.is_system && !isStoryJettisoned(story),
    );
    if (systemStories.length > 0 && liveUserStories.length === 0) {
      const chuziIdx = nebulaTiers.indexOf("chuzi");
      if (chuziIdx >= 0) return chuziIdx;
    }
  }

  const sorted = [...stories].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
  );
  const mostRecentLive = sorted.find(
    (story) => !story.is_system && !isStoryJettisoned(story),
  );
  if (!mostRecentLive) return 0;
  const mostRecentTier = resolveStoryVisibilityTier(mostRecentLive);
  const idx = nebulaTiers.indexOf(mostRecentTier);
  return idx >= 0 ? idx : 0;
}

function clampIndex(value: number, length: number, wrap = true): number {
  if (length === 0) return 0;
  if (wrap) return ((value % length) + length) % length;
  return Math.max(0, Math.min(value, length - 1));
}

export function cosmosNavReducer(
  state: CosmosNavState,
  action: NavAction,
  ctx: NavContext,
): CosmosNavState {
  switch (action.type) {
    case "RESET":
      return initialNavState(action.nebulaIndex ?? state.nebulaIndex);

    case "LEFT":
      return handleHorizontal(state, -1, ctx);

    case "RIGHT":
      return handleHorizontal(state, 1, ctx);

    case "DOWN":
      return handleDrillDown(state, ctx);

    case "UP":
      return handleDrillUp(state, ctx);

    case "JUMP_TO_LEVEL":
      return jumpToLevel(state, action.level, ctx);

    default:
      return state;
  }
}

function handleHorizontal(
  state: CosmosNavState,
  delta: number,
  ctx: NavContext,
): CosmosNavState {
  switch (state.level) {
    case "nebula": {
      const spatialPos = ctx.nebulaSpatialNavOrder.indexOf(state.nebulaIndex);
      const safePos = spatialPos >= 0 ? spatialPos : 0;
      const nextSpatialPos = clampIndex(safePos + delta, ctx.nebulaSpatialNavOrder.length);
      const next = ctx.nebulaSpatialNavOrder[nextSpatialPos];
      return { ...state, nebulaIndex: next, storyIndex: 0, sceneIndex: 0, choiceIndex: 0 };
    }
    case "story": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return state;
      const storiesInTier = ctx.storiesByTier[tier] ?? [];
      if (!storiesInTier.length) return state;
      const next = clampIndex(state.storyIndex + delta, storiesInTier.length);
      return { ...state, storyIndex: next, sceneIndex: 0, choiceIndex: 0 };
    }
    case "scene": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return state;
      const storiesInTier = ctx.storiesByTier[tier] ?? [];
      const story = storiesInTier[state.storyIndex];
      if (!story) return state;
      const storyScenes = ctx.scenesByStory[story.id] ?? [];
      if (!storyScenes.length) return state;
      const next = clampIndex(state.sceneIndex + delta, storyScenes.length);
      return { ...state, sceneIndex: next, choiceIndex: 0 };
    }
    case "choice": {
      const count = ctx.choicesForScene.length;
      if (!count) return state;
      const next = clampIndex(state.choiceIndex + delta, count);
      return { ...state, choiceIndex: next };
    }
    default:
      return state;
  }
}

function handleDrillDown(
  state: CosmosNavState,
  ctx: NavContext,
): CosmosNavState {
  switch (state.level) {
    case "nebula": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return state;
      const storiesInTier = ctx.storiesByTier[tier] ?? [];
      if (!storiesInTier.length) return state;
      return {
        ...state,
        level: "story",
        storyIndex: 0,
        sceneIndex: 0,
        choiceIndex: 0,
        breadcrumb: [
          { level: "nebula", label: ctx.nebulaLabels[state.nebulaIndex] ?? tier, tierIndex: state.nebulaIndex },
        ],
      };
    }
    case "story": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return state;
      const storiesInTier = ctx.storiesByTier[tier] ?? [];
      const story = storiesInTier[state.storyIndex];
      if (!story) return state;
      const storyScenes = ctx.scenesByStory[story.id] ?? [];
      if (!storyScenes.length) return state;
      return {
        ...state,
        level: "scene",
        sceneIndex: 0,
        choiceIndex: 0,
        breadcrumb: [
          { level: "nebula", label: ctx.nebulaLabels[state.nebulaIndex] ?? tier, tierIndex: state.nebulaIndex },
          { level: "story", label: story.title, storyIndex: state.storyIndex },
        ],
      };
    }
    case "scene":
    case "choice":
      return state;
    default:
      return state;
  }
}

function handleDrillUp(
  state: CosmosNavState,
  ctx: NavContext,
): CosmosNavState {
  switch (state.level) {
    case "nebula":
      return state;
    case "story":
      return { ...state, level: "nebula", breadcrumb: [] };
    case "scene":
      return {
        ...state,
        level: "story",
        breadcrumb: state.breadcrumb.slice(0, 1),
      };
    case "choice":
      return {
        ...state,
        level: "scene",
        breadcrumb: state.breadcrumb.slice(0, 2),
      };
    default:
      return state;
  }
}

function jumpToLevel(
  state: CosmosNavState,
  target: NavLevel,
  ctx: NavContext,
): CosmosNavState {
  const levels: NavLevel[] = ["nebula", "story", "scene", "choice"];
  const targetIdx = levels.indexOf(target);
  const currentIdx = levels.indexOf(state.level);
  if (targetIdx >= currentIdx) return state;

  let next = state;
  for (let i = currentIdx; i > targetIdx; i--) {
    next = handleDrillUp(next, ctx);
  }
  return next;
}

export function getStoriesByTier(
  stories: StoryListItem[] | null,
  options?: { includeChuziTier?: boolean },
): Record<NavTier, StoryListItem[]> {
  const result: Record<NavTier, StoryListItem[]> = {
    chuzi: [],
    worldwide: [],
    limited: [],
    private: [],
    jettisoned: [],
  };
  if (!stories) return result;
  for (const story of stories) {
    if (story.is_system) {
      if (options?.includeChuziTier) {
        result.chuzi.push(story);
      }
      continue;
    }
    if (isStoryJettisoned(story)) {
      result.jettisoned.push(story);
      continue;
    }
    const tier = resolveStoryVisibilityTier(story);
    result[tier].push(story);
  }
  result.chuzi.sort((a, b) =>
    (a.system_story_key ?? a.title).localeCompare(b.system_story_key ?? b.title),
  );
  for (const tier of NEBULA_TIERS) {
    if (tier === "jettisoned") {
      result.jettisoned.sort(
        (a, b) => new Date(b.jettisoned_at!).getTime() - new Date(a.jettisoned_at!).getTime(),
      );
    } else {
      result[tier].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
    }
  }
  return result;
}

export function getJettisonedStories(stories: StoryListItem[] | null): StoryListItem[] {
  if (!stories) return [];
  return stories
    .filter(isStoryJettisoned)
    .sort((a, b) => new Date(b.jettisoned_at!).getTime() - new Date(a.jettisoned_at!).getTime());
}

export function currentNavLabel(state: CosmosNavState, ctx: NavContext): string {
  switch (state.level) {
    case "nebula":
      return ctx.nebulaLabels[state.nebulaIndex] ?? "";
    case "story": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return "";
      const storiesInTier = ctx.storiesByTier[tier] ?? [];
      return storiesInTier[state.storyIndex]?.title ?? "";
    }
    case "scene": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return "";
      const storiesInTier = ctx.storiesByTier[tier] ?? [];
      const story = storiesInTier[state.storyIndex];
      if (!story) return "";
      const storyScenes = ctx.scenesByStory[story.id] ?? [];
      return storyScenes[state.sceneIndex]?.title ?? "";
    }
    case "choice":
      return ctx.choicesForScene[state.choiceIndex] ?? "";
    default:
      return "";
  }
}

export function currentLevelItemCount(state: CosmosNavState, ctx: NavContext): number {
  switch (state.level) {
    case "nebula":
      return ctx.nebulaTiers.length;
    case "story": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return 0;
      return (ctx.storiesByTier[tier] ?? []).length;
    }
    case "scene": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return 0;
      const storiesInTier = ctx.storiesByTier[tier] ?? [];
      const story = storiesInTier[state.storyIndex];
      if (!story) return 0;
      return (ctx.scenesByStory[story.id] ?? []).length;
    }
    case "choice":
      return ctx.choicesForScene.length;
    default:
      return 0;
  }
}

export function currentLevelIndex(state: CosmosNavState): number {
  switch (state.level) {
    case "nebula": return state.nebulaIndex;
    case "story": return state.storyIndex;
    case "scene": return state.sceneIndex;
    case "choice": return state.choiceIndex;
    default: return 0;
  }
}

export function canDrillDown(state: CosmosNavState, ctx: NavContext): boolean {
  switch (state.level) {
    case "nebula": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return false;
      return (ctx.storiesByTier[tier] ?? []).length > 0;
    }
    case "story": {
      const tier = tierAtIndex(ctx, state.nebulaIndex);
      if (!tier) return false;
      const storiesInTier = ctx.storiesByTier[tier] ?? [];
      const story = storiesInTier[state.storyIndex];
      if (!story) return false;
      return (ctx.scenesByStory[story.id] ?? []).length > 0;
    }
    case "scene":
    case "choice":
      return false;
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// ALL-mode nav state (tag-cluster based nebulae)
// ---------------------------------------------------------------------------

export const CHUZI_CLUSTER_TAG = "__chuzi__";

export interface AllNavContext {
  nebulaClusters: { tag: string; storyIds: string[]; center: [number, number, number]; radius: number }[];
  chuziNebulaLabel: string;
  storiesByChuzi: StoryListItem[];
  storiesByTag: Record<string, StoryListItem[]>;
  scenesByStory: Record<string, SceneListItem[]>;
  realm: RealmId;
}

function allStoriesForCluster(
  cluster: AllNavContext["nebulaClusters"][number],
  ctx: AllNavContext,
): StoryListItem[] {
  if (cluster.tag === CHUZI_CLUSTER_TAG) {
    return ctx.storiesByChuzi;
  }
  return ctx.storiesByTag[cluster.tag] ?? [];
}

function allNebulaLabel(
  cluster: AllNavContext["nebulaClusters"][number],
  ctx: AllNavContext,
): string {
  if (cluster.tag === CHUZI_CLUSTER_TAG) {
    return ctx.chuziNebulaLabel;
  }
  return `#${cluster.tag}`;
}

export type AllNavLevel = "nebula" | "story" | "scene";

export interface AllCosmosNavState {
  level: AllNavLevel;
  nebulaIndex: number;
  storyIndex: number;
  sceneIndex: number;
  breadcrumb: BreadcrumbEntry[];
}

export type AllNavAction =
  | { type: "LEFT" }
  | { type: "RIGHT" }
  | { type: "DOWN" }
  | { type: "UP" }
  | { type: "JUMP_TO_LEVEL"; level: AllNavLevel }
  | { type: "JUMP_TO_NEBULA"; index: number }
  | { type: "SELECT_STORY"; storyId: string }
  | { type: "RESET"; nebulaIndex?: number };

/** Focus nav on a story constellation clicked in the ALL cosmos canvas. */
export function allNavStateForStory(
  storyId: string,
  ctx: AllNavContext,
): AllCosmosNavState | null {
  for (let nebulaIndex = 0; nebulaIndex < ctx.nebulaClusters.length; nebulaIndex++) {
    const cluster = ctx.nebulaClusters[nebulaIndex];
    const stories = allStoriesForCluster(cluster, ctx);
    const storyIndex = stories.findIndex((story) => story.id === storyId);
    if (storyIndex < 0) continue;
    return {
      level: "story",
      nebulaIndex,
      storyIndex,
      sceneIndex: 0,
      breadcrumb: [
        { level: "nebula", label: allNebulaLabel(cluster, ctx), tierIndex: nebulaIndex },
      ],
    };
  }
  return null;
}

export function initialAllNavState(nebulaIndex = 0): AllCosmosNavState {
  return {
    level: "nebula",
    nebulaIndex,
    storyIndex: 0,
    sceneIndex: 0,
    breadcrumb: [],
  };
}

function allClampIndex(value: number, length: number): number {
  if (length === 0) return 0;
  return ((value % length) + length) % length;
}

export function allCosmosNavReducer(
  state: AllCosmosNavState,
  action: AllNavAction,
  ctx: AllNavContext,
): AllCosmosNavState {
  switch (action.type) {
    case "RESET":
      return initialAllNavState(action.nebulaIndex ?? state.nebulaIndex);

    case "JUMP_TO_NEBULA":
      return {
        ...initialAllNavState(action.index),
      };

    case "SELECT_STORY": {
      const next = allNavStateForStory(action.storyId, ctx);
      return next ?? state;
    }

    case "LEFT":
      return allHandleHorizontal(state, -1, ctx);

    case "RIGHT":
      return allHandleHorizontal(state, 1, ctx);

    case "DOWN":
      return allHandleDrillDown(state, ctx);

    case "UP":
      return allHandleDrillUp(state);

    case "JUMP_TO_LEVEL":
      return allJumpToLevel(state, action.level);

    default:
      return state;
  }
}

function allHandleHorizontal(
  state: AllCosmosNavState,
  delta: number,
  ctx: AllNavContext,
): AllCosmosNavState {
  switch (state.level) {
    case "nebula": {
      const count = ctx.nebulaClusters.length;
      if (count === 0) return state;
      const next = allClampIndex(state.nebulaIndex + delta, count);
      return { ...state, nebulaIndex: next, storyIndex: 0, sceneIndex: 0 };
    }
    case "story": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return state;
      const stories = allStoriesForCluster(cluster, ctx);
      if (!stories.length) return state;
      const next = allClampIndex(state.storyIndex + delta, stories.length);
      return { ...state, storyIndex: next, sceneIndex: 0 };
    }
    case "scene": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return state;
      const stories = allStoriesForCluster(cluster, ctx);
      const story = stories[state.storyIndex];
      if (!story) return state;
      const scenes = ctx.scenesByStory[story.id] ?? [];
      if (!scenes.length) return state;
      const next = allClampIndex(state.sceneIndex + delta, scenes.length);
      return { ...state, sceneIndex: next };
    }
    default:
      return state;
  }
}

function allHandleDrillDown(
  state: AllCosmosNavState,
  ctx: AllNavContext,
): AllCosmosNavState {
  switch (state.level) {
    case "nebula": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return state;
      const stories = allStoriesForCluster(cluster, ctx);
      if (!stories.length) return state;
      return {
        ...state,
        level: "story",
        storyIndex: 0,
        sceneIndex: 0,
        breadcrumb: [
          { level: "nebula", label: allNebulaLabel(cluster, ctx), tierIndex: state.nebulaIndex },
        ],
      };
    }
    case "story": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return state;
      const stories = allStoriesForCluster(cluster, ctx);
      const story = stories[state.storyIndex];
      if (!story) return state;
      const scenes = ctx.scenesByStory[story.id] ?? [];
      if (!scenes.length) return state;
      return {
        ...state,
        level: "scene",
        sceneIndex: 0,
        breadcrumb: [
          { level: "nebula", label: allNebulaLabel(cluster, ctx), tierIndex: state.nebulaIndex },
          { level: "story", label: story.title, storyIndex: state.storyIndex },
        ],
      };
    }
    case "scene":
      return state;
    default:
      return state;
  }
}

function allHandleDrillUp(state: AllCosmosNavState): AllCosmosNavState {
  switch (state.level) {
    case "nebula":
      return state;
    case "story":
      return { ...state, level: "nebula", breadcrumb: [] };
    case "scene":
      return {
        ...state,
        level: "story",
        breadcrumb: state.breadcrumb.slice(0, 1),
      };
    default:
      return state;
  }
}

function allJumpToLevel(
  state: AllCosmosNavState,
  target: AllNavLevel,
): AllCosmosNavState {
  const levels: AllNavLevel[] = ["nebula", "story", "scene"];
  const targetIdx = levels.indexOf(target);
  const currentIdx = levels.indexOf(state.level);
  if (targetIdx >= currentIdx) return state;

  let next = state;
  for (let i = currentIdx; i > targetIdx; i--) {
    next = allHandleDrillUp(next);
  }
  return next;
}

export function allCurrentNavLabel(state: AllCosmosNavState, ctx: AllNavContext): string {
  switch (state.level) {
    case "nebula": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      return cluster ? allNebulaLabel(cluster, ctx) : "";
    }
    case "story": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return "";
      const stories = allStoriesForCluster(cluster, ctx);
      return stories[state.storyIndex]?.title ?? "";
    }
    case "scene": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return "";
      const stories = allStoriesForCluster(cluster, ctx);
      const story = stories[state.storyIndex];
      if (!story) return "";
      const scenes = ctx.scenesByStory[story.id] ?? [];
      return scenes[state.sceneIndex]?.title ?? "";
    }
    default:
      return "";
  }
}

export function allCurrentLevelItemCount(state: AllCosmosNavState, ctx: AllNavContext): number {
  switch (state.level) {
    case "nebula":
      return ctx.nebulaClusters.length;
    case "story": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return 0;
      return allStoriesForCluster(cluster, ctx).length;
    }
    case "scene": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return 0;
      const stories = allStoriesForCluster(cluster, ctx);
      const story = stories[state.storyIndex];
      if (!story) return 0;
      return (ctx.scenesByStory[story.id] ?? []).length;
    }
    default:
      return 0;
  }
}

export function allCurrentLevelIndex(state: AllCosmosNavState): number {
  switch (state.level) {
    case "nebula": return state.nebulaIndex;
    case "story": return state.storyIndex;
    case "scene": return state.sceneIndex;
    default: return 0;
  }
}

export function allCanDrillDown(state: AllCosmosNavState, ctx: AllNavContext): boolean {
  switch (state.level) {
    case "nebula": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return false;
      return allStoriesForCluster(cluster, ctx).length > 0;
    }
    case "story": {
      const cluster = ctx.nebulaClusters[state.nebulaIndex];
      if (!cluster) return false;
      const stories = allStoriesForCluster(cluster, ctx);
      const story = stories[state.storyIndex];
      if (!story) return false;
      return (ctx.scenesByStory[story.id] ?? []).length > 0;
    }
    case "scene":
      return false;
    default:
      return false;
  }
}
